import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryCustomerEntity } from "../../../../query/onboarding/queries/useQueryCustomerEntity";
import { useUpdateActivity } from "../../../../query/users/mutations";
import {
  useActivities,
  useMemberProfile,
  useUserProfile
} from "../../../../query/users/queries";
import { avatarTaxWithholding } from "../../../../shared/constants/avatars";
import { useWSAnalytics } from "../../../../utils/WSAnalytics";
import { PATH_ONBOARDING_TAX_WITHHOLDINGS } from "../../../Onboarding";
import { selectorProposeTaxWithholdings } from "../../../Taxes/selectors/selectorProposeTaxWithholdings";
import { Promotion, PromotionVariant } from "./Promotion";

const TAX_WITHHOLDING_VARIANTS: PromotionVariant[] = [
  {
    avatar: avatarTaxWithholding,
    title: "Tax withholdings",
    quote: {
      icon: "bulb",
      title: "Stop stressing about quarterly taxes",
      content: (
        <>
          Tired of guessing how much to save? Wingspan automatically sets aside
          the right amount from every payment, so you're always prepared for tax
          time. <b>Pay the IRS in 1-click each quarter.</b>
        </>
      )
    },
    action: {
      label: "Automate my tax savings"
    }
  },
  {
    avatar: avatarTaxWithholding,
    title: "Tax withholdings",
    quote: {
      icon: "life",
      title: "Your peace of mind, automated",
      content: (
        <>
          Never worry about having enough for taxes again. We'll split a
          percentage of your income into a dedicated tax account, removing the
          guesswork and manual effort.{" "}
          <b>Focus on your work, not tax compliance.</b>
        </>
      )
    },
    action: {
      label: "Set up my withholdings"
    }
  },
  {
    avatar: avatarTaxWithholding,
    title: "Tax withholdings",
    quote: {
      icon: "dollar-circle",
      title: "See your true spendable income",
      content: (
        <>
          By separating tax funds the moment you're paid,{" "}
          <b>you get a clear picture of what's truly yours to spend</b>.
          Eliminate financial confusion and the risk of unexpected tax bills.
        </>
      )
    },
    action: {
      label: "Set up my withholdings"
    }
  }
];

export const useShowTaxWithholdingsPromotion = () => {
  const userId = useUserId();
  const queryUser = useUserProfile(userId);
  const queryMember = useMemberProfile(userId);
  const customerEntity = useQueryCustomerEntity();
  const queryActivity = useActivities(userId);
  const queryFeatureFlags = useFeatureFlags();
  const queryInternalAccounts = useQueryInternalAccounts();

  return useMemo(() => {
    if (
      !queryFeatureFlags.data ||
      !queryFeatureFlags.data.taxWithholdingDashboardPromotion
    ) {
      return false;
    }

    if (
      !queryActivity.data ||
      queryActivity.data.events.dashboardTaxWithholdingsPromotionDismissed
    ) {
      return false;
    }

    return selectorProposeTaxWithholdings(
      queryUser.data,
      queryMember.data,
      customerEntity.data,
      queryInternalAccounts.data
    );
  }, [
    queryFeatureFlags.data,
    queryActivity.data,
    queryUser.data,
    queryMember.data,
    customerEntity.data,
    queryInternalAccounts.data
  ]);
};

export const CardTaxWithholdingPromotion: React.FC = () => {
  const userId = useUserId();
  const history = useHistory();
  const analytics = useWSAnalytics();
  const [dismiss, dismissMeta] = useUpdateActivity(userId);

  const handleDismiss = () => {
    dismiss({
      events: {
        dashboardTaxWithholdingsPromotionDismissed: true
      }
    });
  };

  const showTaxWithholdingsPromotion = useShowTaxWithholdingsPromotion();

  // Instantly hide promotion while updating activity
  if (dismissMeta.isLoading) {
    return null;
  }

  if (!showTaxWithholdingsPromotion) {
    return null;
  }

  return (
    <Promotion
      key="tax-withholdings"
      variants={TAX_WITHHOLDING_VARIANTS}
      onDismiss={handleDismiss}
      onAction={variantIndex => {
        analytics.track.other("Dashboard Tax Withholding Promotion Click", {
          variantIndex
        });

        history.push(PATH_ONBOARDING_TAX_WITHHOLDINGS);
      }}
    />
  );
};
